export type TalkFlags = {
  localMic: boolean;
  localSpeaker: boolean;
  remoteMic: boolean;
  remoteSpeaker: boolean;
};

export function talkDirections(flags: TalkFlags): { send: boolean; hear: boolean } {
  return {
    send: flags.localMic && flags.remoteSpeaker,
    hear: flags.remoteMic && flags.localSpeaker,
  };
}

function floatToPcm16k(input: Float32Array, inRate: number): Uint8Array {
  const ratio = inRate / 16000;
  if (!Number.isFinite(ratio) || ratio <= 0) return new Uint8Array();
  const outLen = Math.max(0, Math.floor(input.length / ratio));
  const pcm = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j += 1) {
      sum += input[j];
      count += 1;
    }
    const sample = count ? sum / count : 0;
    const clipped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  return new Uint8Array(pcm.buffer);
}

export class RemoteTalk {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private proc: ScriptProcessorNode | null = null;
  private mute: GainNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private playAt = 0;
  private send = false;
  private hear = false;
  private ws: WebSocket | null = null;

  async start(ws: WebSocket, flags: TalkFlags): Promise<void> {
    this.stop();
    const dir = talkDirections(flags);
    if (!dir.send && !dir.hear) return;
    this.ws = ws;
    this.send = dir.send;
    this.hear = dir.hear;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) throw new Error('이 브라우저는 음성을 지원하지 않음');
    this.ctx = new Ctx();
    await this.ctx.resume();
    if (!dir.send) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: false,
    });
    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.proc = this.ctx.createScriptProcessor(4096, 1, 1);
    this.proc.onaudioprocess = (ev) => {
      if (!this.send || !this.ctx || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      const pcm = floatToPcm16k(ev.inputBuffer.getChannelData(0), this.ctx.sampleRate);
      if (pcm.length < 2) return;
      const packet = new Uint8Array(1 + pcm.length);
      packet[0] = 0xa1;
      packet.set(pcm, 1);
      this.ws.send(packet);
    };
    this.mute = this.ctx.createGain();
    this.mute.gain.value = 0;
    this.source.connect(this.proc);
    this.proc.connect(this.mute);
    this.mute.connect(this.ctx.destination);
  }

  update(flags: TalkFlags): void {
    const dir = talkDirections(flags);
    this.send = dir.send;
    this.hear = dir.hear;
    if (!this.hear) this.playAt = 0;
  }

  play(pcm: Uint8Array): void {
    if (!this.hear || !this.ctx || pcm.length < 2) return;
    const samples = pcm.length >> 1;
    const audioBuf = this.ctx.createBuffer(1, samples, 16000);
    const channel = audioBuf.getChannelData(0);
    const view = new DataView(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    for (let i = 0; i < samples; i += 1) {
      channel[i] = view.getInt16(i * 2, true) / 32768;
    }
    const node = this.ctx.createBufferSource();
    node.buffer = audioBuf;
    node.connect(this.ctx.destination);
    const now = this.ctx.currentTime;
    if (this.playAt < now + 0.02) this.playAt = now + 0.06;
    if (this.playAt - now > 0.45) this.playAt = now + 0.06;
    node.start(this.playAt);
    this.playAt += audioBuf.duration;
  }

  stop(): void {
    this.send = false;
    this.hear = false;
    this.ws = null;
    try {
      this.proc?.disconnect();
      this.source?.disconnect();
      this.mute?.disconnect();
    } catch {
      /* ignore */
    }
    this.proc = null;
    this.source = null;
    this.mute = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    const ctx = this.ctx;
    this.ctx = null;
    this.playAt = 0;
    if (ctx) void ctx.close().catch(() => undefined);
  }
}
